import { useCallback, useMemo } from 'react';
import { useLanguage, Lang } from '../contexts/LanguageContext';
import common from './common';
import chat from './chat';
import admin from './admin';
import pages from './pages';
import tasks from './tasks';
import workflows from './workflows';
import credits from './credits';
import announcements from './announcements';
import governance from './governance';
import agentPlatform from './agentPlatform';

/**
 * App-wide translations.
 *
 * Each area keeps its strings in its own file ({ en, ar } with flat,
 * namespaced keys like 'chat.newChat'), so parallel work on different pages
 * never edits the same dictionary. Every file types its Arabic table as
 * Record<keyof typeof en, string>, so a missing translation is a compile
 * error, not a silently English label.
 *
 * Placeholders: t('chat.matches', { n: 3 }) with "{n} matches" in the table.
 */
const en = { ...common.en, ...chat.en, ...admin.en, ...pages.en, ...tasks.en, ...workflows.en, ...credits.en, ...announcements.en, ...governance.en, ...agentPlatform.en };
const ar = { ...common.ar, ...chat.ar, ...admin.ar, ...pages.ar, ...tasks.ar, ...workflows.ar, ...credits.ar, ...announcements.ar, ...governance.ar, ...agentPlatform.ar };

export type TKey = keyof typeof en;
export type TVars = Record<string, string | number>;

const TABLES: Record<Lang, Record<TKey, string>> = { en, ar };

export function translate(lang: Lang, key: TKey, vars?: TVars): string {
  const template = TABLES[lang][key] ?? TABLES.en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name) => (name in vars ? String(vars[name]) : m));
}

/**
 * BCP-47 locale for dates and numbers. Arabic keeps Latin digits (…-nu-latn):
 * figures in this app are business data that people compare with
 * spreadsheets and reports, which use 0–9.
 */
export const localeFor = (lang: Lang) => (lang === 'ar' ? 'ar-SA-u-nu-latn-ca-gregory' : 'en-US');

export function useT() {
  const { lang } = useLanguage();
  const t = useCallback((key: TKey, vars?: TVars) => translate(lang, key, vars), [lang]);
  return useMemo(() => {
    const locale = localeFor(lang);
    return {
      t,
      lang,
      dir: (lang === 'ar' ? 'rtl' : 'ltr') as 'rtl' | 'ltr',
      locale,
      formatDate: (d: string | number | Date, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' }) =>
        new Date(d).toLocaleDateString(locale, opts),
      formatDateTime: (d: string | number | Date, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }) =>
        new Date(d).toLocaleString(locale, opts),
      formatTime: (d: string | number | Date) =>
        new Date(d).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
      formatNumber: (n: number, opts?: Intl.NumberFormatOptions) => n.toLocaleString(locale, opts),
    };
  }, [t, lang]);
}
